import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { NavLink, Route, Routes } from 'react-router-dom';

import { useSubscriptionDetails } from '../../hooks/apiHooks/subscriptionHooks';

import { getOrganizationId, isUserAdmin } from '@utils/getOrganization.ts';
import { Features } from '../../config';
import { OidcUser } from '../../types/oidcUser';
import BillingTab from './components/BillingTab';
import MembersTab from './components/MembersTab';
import OverviewTab from './components/OverviewTab';

import {
  useUpdateDefaultOrganization,
  useUpdateUser,
} from '@hooks/apiHooks/userHooks';
import { useTokenRefresh } from '@hooks/useTokenRefresh';
import { extraQueryParams } from '@utils/constants';
import { analytics } from '../../utils/analytics';

export default function AccountPage() {
  const { t } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isUpdatingDefaultOrganization, setIsUpdatingDefaultOrganization] =
    useState(false);

  const auth = useAuth();
  const { user, isAuthenticated } = auth;
  const { mutateAsync: updateUser } = useUpdateUser();

  const { mutateAsync: updateDefaultOrganization } =
    useUpdateDefaultOrganization();

  const { refreshAccessToken } = useTokenRefresh();

  const orgId = getOrganizationId(user as OidcUser);
  const isCurrentUserAdmin = isUserAdmin(user as OidcUser, orgId);

  const {
    data: subscriptionDetails,
    isFetching,
    refetch,
    error: subscriptionError,
    isError,
  } = useSubscriptionDetails({ organization_id: orgId }, isAuthenticated);

  useEffect(() => {
    if (isError) {
      if (subscriptionError.status === 401) {
        void auth.signinRedirect({
          extraQueryParams,
          state: { returnUrl: location.pathname },
        });
      }
    }
  }, [isError, subscriptionError]);

  const handleUpdateDefaultOrganization = async (organizationId: string) => {
    try {
      setIsUpdatingDefaultOrganization(true);
      updateDefaultOrganization({
        organizationId,
      })
        .then(() => refreshAccessToken(user?.refresh_token as string))
        .catch((error) => {
          console.error('Error updating default organization:', error);
        })
        .finally(() => {
          setIsUpdatingDefaultOrganization(false);
        });
    } catch (error) {
      console.error('Error updating default organization:', error);
    }
  };

  const handleSubmitForm = (data: { firstName: string; lastName: string }) => {
    if (isAuthenticated) {
      // Determine which fields were updated
      const fieldsUpdated: string[] = [];
      const currentFirstName = user?.profile.firstName as string;
      const currentLastName = user?.profile.lastName as string;

      if (data.firstName !== currentFirstName) {
        fieldsUpdated.push('First Name');
      }
      if (data.lastName !== currentLastName) {
        fieldsUpdated.push('Last Name');
      }

      // Track profile_details_saved event only if there are actual changes
      if (fieldsUpdated.length > 0) {
        analytics.logEvent('profile_details_saved', {
          user_id: user?.profile.sub || '',
          fields_updated: fieldsUpdated.join(', '),
        });
      }

      setIsUpdating(true);
      updateUser({
        userSub: user?.profile.sub || '',
        firstName: data.firstName,
        lastName: data.lastName,
      })
        .then(() => refreshAccessToken(user?.refresh_token as string))
        .finally(() => {
          setIsUpdating(false);
        });
    } else {
      void auth.signinRedirect({
        extraQueryParams,
        state: { returnUrl: location.pathname },
      });
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <p className="text-white">{t('account.signInRequired')}</p>
      </div>
    );
  }

  const baseClasses = 'leading-[150%] rounded-md font-medium px-3 py-1';
  return (
    <div className="w-full h-full overflow-auto pb-20 pt-0 sm:pt-[20px] na-scroll-container">
      <div className={`max-w-7xl mx-auto  sm:px-6 px-3 lg:px-8`}>
        {/* Header */}
        <div className="flex flex-col mb-6">
          <h1 className="text-[32px] sm:text-[40px] font-normal leading-[140%] text-left text-[#fcfcfc] font-['DM_Serif_Text']">
            {t('account.title')}
          </h1>
          <div className="text-[14px] font-normal leading-[140%] text-left text-white/80 pb-4 font-[NapsterSans]">
            {t('account.subtitle')}
          </div>
        </div>

        <nav className="flex gap-2 sm:mb-8 mb-4 rounded-md">
          <NavLink
            to="/account"
            className={({ isActive }) =>
              `${baseClasses} ${
                isActive ? 'bg-white text-black' : 'text-white'
              }`
            }
            end>
            {t('account.tabOverview')}
          </NavLink>
          {isCurrentUserAdmin && (
            <NavLink
              to="/account/billing"
              className={({ isActive }) =>
                `${baseClasses} ${
                  isActive ? 'bg-white text-black' : 'text-white'
                }`
              }>
              {t('account.tabBilling')}
            </NavLink>
          )}
          {Features.enableOrganizationManagement && (
            <NavLink
              to="/account/members"
              className={({ isActive }) =>
                `${baseClasses} ${
                  isActive ? 'bg-white text-black' : 'text-white'
                }`
              }>
              {t('account.tabMembers')}
            </NavLink>
          )}
        </nav>
        <Routes>
          <Route
            path="/"
            element={
              <OverviewTab
                defaultOrgId={orgId}
                userFirstName={user?.profile.firstName as string}
                userLastName={user?.profile.lastName as string}
                email={user?.profile.email as string}
                subscriptionDetails={subscriptionDetails}
                onSubmitForm={handleSubmitForm}
                onUpdateDefaultOrganization={handleUpdateDefaultOrganization}
                isFetching={isFetching}
                isUpdating={isUpdating}
                isUpdatingDefaultOrganization={isUpdatingDefaultOrganization}
                organizations={(user as OidcUser)?.profile.organization}
              />
            }
          />
          {isCurrentUserAdmin && (
            <Route
              path="/billing"
              element={
                <BillingTab
                  subscriptionDetails={subscriptionDetails}
                  isFetching={isFetching}
                  refetch={() => {
                    void refetch();
                  }}
                />
              }
            />
          )}
          {Features.enableOrganizationManagement && (
            <Route
              path="/members"
              element={
                <MembersTab
                  orgId={orgId}
                  isCurrentUserAdmin={isCurrentUserAdmin}
                />
              }
            />
          )}
        </Routes>
      </div>
    </div>
  );
}
